import {
  DEFAULT_TILE_DISPLAY,
  SLIDER_TILE_DISPLAY,
} from "~/hec/components/organisms/dynamic-product-tile/display-defaults";
import type { DynamicProductTileLayout } from "~/hec/components/organisms/dynamic-product-tile/types";

/**
 * Builder inputs for `DynamicProductTile` (POC). The SKU, feature and tile-option
 * groups are exported so `FeaturedProductSlider` declares exactly the same fields
 * (one source of truth — same pattern as SFCC-11246).
 */

/** Which product — the SFCC SKU / product id, typed in. */
export const productChoiceInputs = [
  {
    name: "productId",
    friendlyName: "SKU",
    type: "string",
    helperText: "SFCC product id (master, variant or simple), e.g. 42362",
  },
];

/**
 * Per-product Feature-layout content. The two block labels ("Great for",
 * "Key benefits") are fixed in the design, so authors only write the text.
 * No `showIf` here. The single tile adds one (its layout is a top-level
 * input); the slider's list rows deliberately have none — see
 * `featured-product-slider/inputs.ts`.
 */
export const featureContentInputs = [
  {
    name: "greatFor",
    friendlyName: "Great for",
    type: "string",
    helperText: "input will only show on feature tile layout option.",
  },
  {
    name: "keyBenefits",
    friendlyName: "Key benefits",
    type: "string",
    helperText: "input will only show on feature tile layout option.",
  },
  {
    name: "image",
    friendlyName: "Custom photo (optional)",
    type: "file",
    allowedFileTypes: ["png", "jpg", "jpeg", "webp", "svg"],
    helperText: "input will only show on feature tile layout option.",
  },
];

/**
 * Feature vs standard card. A factory because the two blocks start on
 * different layouts (`DEFAULT_TILE_DISPLAY` / `SLIDER_TILE_DISPLAY`).
 */
export const tileLayoutInput = (defaultLayout: DynamicProductTileLayout) => ({
  name: "layout",
  friendlyName: "Tile layout",
  type: "text",
  enum: [
    { label: "Feature (large image + benefit blocks)", value: "feature" },
    { label: "Standard storefront tile", value: "standard" },
  ],
  defaultValue: defaultLayout,
});

/** The parts of a tile an author can switch on or off. */
const tilePartInputs = [
  {
    name: "showBadges",
    friendlyName: "Show badges",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showBadges,
  },
  {
    name: "showPrice",
    friendlyName: "Show price",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showPrice,
  },
  {
    name: "showRatings",
    friendlyName: "Show ratings",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showRatings,
  },
  {
    name: "showAddToCart",
    friendlyName: "Show Add to Cart",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showAddToCart,
  },
  {
    name: "showQuickView",
    friendlyName: "Show Quick View",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showQuickView,
    helperText:
      "Products with sizes/flavors need Quick View for Add to Cart to work.",
  },
  {
    name: "showFavorites",
    friendlyName: "Show favorites heart",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showFavorites,
  },
  {
    name: "showMessaging",
    friendlyName: "Show promo messaging",
    type: "boolean",
    defaultValue: DEFAULT_TILE_DISPLAY.showMessaging,
    helperText: "Standard layout only.",
  },
  {
    name: "borderColor",
    friendlyName: "Border color",
    type: "color",
  },
];

/** How the slider's tiles look — applied to every tile at once. */
export const sliderTileDisplayInputs = [
  tileLayoutInput(SLIDER_TILE_DISPLAY.layout),
  ...tilePartInputs,
];

/**
 * Single tile: SKU, then layout, then the Feature-only content — hidden for
 * the standard tile, which never renders it. Top-level `options` here IS the
 * block, so a layout `showIf` works (unlike inside the slider's list rows).
 * Shown only for `feature`: an unset layout is the standard default.
 */
export const dynamicProductTileInputs = [
  ...productChoiceInputs,
  tileLayoutInput(DEFAULT_TILE_DISPLAY.layout),
  ...featureContentInputs.map(input => ({
    ...input,
    showIf: `options.get('layout') === 'feature'`,
  })),
  ...tilePartInputs,
  // ── Layout ───────────────────────────────────────────────────────────
  {
    name: "maxWidth",
    friendlyName: "Max width (px)",
    type: "number",
    min: 120,
    helperText: "Leave blank to fill the column.",
  },
  {
    name: "align",
    type: "text",
    enum: [
      { label: "Left", value: "left" },
      { label: "Center", value: "center" },
      { label: "Right", value: "right" },
    ],
    defaultValue: "center",
  },
  // ── Analytics ────────────────────────────────────────────────────────
  {
    name: "listName",
    friendlyName: "Analytics list name",
    type: "string",
    advanced: true,
    helperText:
      "GA4 item_list_name for clicks on this tile. Defaults to the product id.",
  },
];
